import axios from "axios";

/**
 * WhatsApp Broadcast API (store owners/admins of the active store — backend:
 * WaBroadcastController).
 *
 * Every call is QUIET: these surfaces poll and report their own failures
 * inline, so the global error toast would only repeat them (same reasoning as
 * CoobyVmSettings). 401 refresh-and-replay still runs.
 *
 * Every call carries `account_id` — which of the person's numbers it is about.
 * Omitted, the backend uses their first number.
 */
const API = "/api/admin/wa-broadcast";

const data = (p) => p.then((res) => res.data);
const opts = (accountId) => ({
  metadata: { silent: true },
  ...(accountId ? { params: { account_id: accountId } } : {}),
});

/** The disclosure statements the backend requires (WaBroadcastService::REQUIRED_RISKS). */
export const REQUIRED_RISKS = ["third_party", "ban_risk", "no_reinstatement", "no_liability", "best_practice"];

export const waBroadcastApi = {
  overview: (a) => data(axios.get(API, opts(a))),
  update: (patch, a) => data(axios.put(API, patch, opts(a))),

  numberMode: () => data(axios.get(`${API}/number-mode`, opts())),
  /** Owner Admin → WhatsApp Broadcast: any subset of { mode, include_link, closing_lines }. */
  setNumberMode: (patch) => data(axios.put(`${API}/number-mode`, typeof patch === "string" ? { mode: patch } : patch, opts())),

  startLink: (consent, a) => data(axios.post(`${API}/link`, { consent }, opts(a))),
  linkStatus: (a) => data(axios.get(`${API}/link`, opts(a))),
  pairingCode: (phone, a) => data(axios.post(`${API}/link/code`, { phone }, opts(a))),
  unlink: (forget = false, a) => data(axios.post(`${API}/unlink`, { forget }, opts(a))),

  /** "Add another number": a new unlinked number; answers with ITS overview (numbers.selected_id). */
  addNumber: () => data(axios.post(`${API}/numbers`, null, opts())),
  /** Remove an extra, unlinked number; answers with the first number's overview. */
  removeNumber: (id) => data(axios.delete(`${API}/numbers/${id}`, opts())),

  groups: (a) => data(axios.get(`${API}/groups`, opts(a))),
  syncGroups: (a) => data(axios.post(`${API}/groups/sync`, null, opts(a))),
  updateGroup: (id, patch, a) => data(axios.put(`${API}/groups/${id}`, patch, opts(a))),

  lists: (a) => data(axios.get(`${API}/lists`, opts(a))),
  createList: (body, a) => data(axios.post(`${API}/lists`, body, opts(a))),
  updateList: (id, patch, a) => data(axios.put(`${API}/lists/${id}`, patch, opts(a))),
  deleteList: (id, a) => data(axios.delete(`${API}/lists/${id}`, opts(a))),

  broadcasts: (a) => data(axios.get(`${API}/broadcasts`, opts(a))),
  broadcast: (id, a) => data(axios.get(`${API}/broadcasts/${id}`, opts(a))),
  /** The item's latest finished broadcast for "Send again": { last: {...} | null }. */
  lastBroadcast: (subjectType, subjectId, a) =>
    data(axios.get(`${API}/broadcasts/last`, { metadata: { silent: true }, params: { subject_type: subjectType, subject_id: subjectId, ...(a ? { account_id: a } : {}) } })),
  send: (body, a) => data(axios.post(`${API}/broadcasts`, body, opts(a))),
  cancel: (id, a) => data(axios.post(`${API}/broadcasts/${id}/cancel`, null, opts(a))),

  /** A photo to send instead of the item's own. Returns { id }. */
  uploadPhoto: (file) => {
    const form = new FormData();
    form.append("file", file);
    return data(axios.post(`${API}/photos`, form, opts()));
  },

  /** Owner Admin → Message templates: { templates: { listing|order|sell: { template, custom, default, variables } } }. */
  templates: () => data(axios.get(`${API}/templates`, opts())),
  /** Any subset of { listing, order, sell }; NULL = back to the built-in one. */
  setTemplates: (patch) => data(axios.put(`${API}/templates`, { templates: patch }, opts())),
  /** The store's own caption for one item, or { caption: null } to keep the built-in one. */
  caption: (subjectType, subjectId) =>
    data(axios.get(`${API}/caption`, { metadata: { silent: true }, params: { subject_type: subjectType, subject_id: subjectId } })),

  /** Retail Circle Admin → WhatsApp Broadcast (master admins). */
  rules: () => data(axios.get(`${API}/rules`, opts())),
  setRules: (patch) => data(axios.put(`${API}/rules`, patch, opts())),
};

/**
 * "Send via WhatsApp": the store's own message template for this item
 * (Owner Admin → Message templates), or `fallback` — the built-in text — when
 * it has none. Never throws, and gives up after 4 s, so sharing is never
 * blocked by it.
 */
export async function storeCaption(subjectType, subjectId, fallback) {
  try {
    const res = await axios.get(`${API}/caption`, {
      metadata: { silent: true },
      timeout: 4000,
      params: { subject_type: subjectType, subject_id: subjectId },
    });
    return res.data?.caption || fallback;
  } catch {
    return fallback;
  }
}

/** The first human-readable message out of an axios error. */
export function apiError(err, fallback) {
  const body = err?.response?.data;
  if (body?.errors) {
    const first = Object.values(body.errors).flat()[0];
    if (first) return first;
  }
  return body?.error || body?.message || fallback;
}
